import { AppShell as MantineAppShell } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { useMedplum, useMedplumProfile } from '@medplum/react-hooks';
import { ReactNode, Suspense, useEffect } from 'react';
import classes from './AppShell.module.css';
import { Header } from './Header';
import { Navbar, NavbarMenu } from './Navbar';
import { ErrorBoundary, Loading } from '@medplum/react';
import { useLocalStorage } from '@mantine/hooks';
import { useUserPreferences } from '@hooks/useUserPreferences';

export interface AppShellProps {
  readonly logo: ReactNode;
  readonly pathname?: string;
  readonly searchParams?: URLSearchParams;
  readonly headerSearchDisabled?: boolean;
  readonly version?: string;
  readonly menus?: NavbarMenu[];
  readonly children: ReactNode;
  readonly displayAddBookmark?: boolean;
  readonly resourceTypeSearchDisabled?: boolean;
  readonly notifications?: ReactNode;
  readonly disableHeaderPadding?: boolean;
}

export function AppShell(props: AppShellProps): JSX.Element {
  const [navbarOpen, setNavbarOpen] = useLocalStorage<boolean>({
    key: 'navbarOpen',
    defaultValue: true,
  });

  const { savePreferences } = useUserPreferences();

  const medplum = useMedplum();
  const profile = useMedplumProfile();

  useEffect(() => {
    function eventListener(): void {
      showNotification({
        id: 'offline',
        color: 'red',
        message: 'No connection to server',
        autoClose: false,
      });
    }
    medplum.addEventListener('offline', eventListener);
    return () => medplum.removeEventListener('offline', eventListener);
  }, [medplum]);

  if (medplum.isLoading()) {
    return <Loading />;
  }

  const setNavbarOpenWrapper = (state: boolean) => {
    setNavbarOpen(state);
    savePreferences();
  };

  return (
    <MantineAppShell
      header={{ height: 60 }}
      navbar={{
        width: navbarOpen ? 250 : 74,
        breakpoint: 'sm',
        collapsed: {
          desktop: !profile,
          mobile: !profile || !navbarOpen,
        },
      }}
      padding={0}
    >
      {profile && (
        <Header
          pathname={props.pathname}
          searchParams={props.searchParams}
          headerSearchDisabled={props.headerSearchDisabled}
          logo={props.logo}
          version={props.version}
          navbarToggle={() => {
            setNavbarOpenWrapper(!navbarOpen);
          }}
          notifications={props.notifications}
        />
      )}
      {profile ? (
        <Navbar
          iconsOnly={!navbarOpen}
          pathname={props.pathname}
          searchParams={props.searchParams}
          menus={props.menus}
          closeNavbar={() => {
            setNavbarOpenWrapper(false);
          }}
          displayAddBookmark={props.displayAddBookmark}
          resourceTypeSearchDisabled={props.resourceTypeSearchDisabled}
        />
      ) : undefined}
      <MantineAppShell.Main
        className={`${classes.main} ${
          props.disableHeaderPadding ? classes.noPadding : ''
        }`}
      >
        <ErrorBoundary>
          <Suspense fallback={<Loading />}>{props.children}</Suspense>
        </ErrorBoundary>
      </MantineAppShell.Main>
    </MantineAppShell>
  );
}
